import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import React from 'react';
import isEqual from 'react-fast-compare';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Grid, AccessibleGridCell, GridCellSummary, GridSummary} from 'constants/SampleConstants';
import {checkSamplesForStatusWarnings} from 'inventory/sample-status/SampleStatusWarning';
import SampleStatusWarningBannerStack from 'inventory/sample-status/SampleStatusWarningBannerStack';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {
  TransferPaneSampleStatusState,
  UpdateTransferModalStateHandler,
  UpdateTransferPaneSampleStatusStateHandler,
} from 'samples/transfers/TransferModalUtil';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';

import {isCellOccupiedAndAccessible} from '../grids/GridCellsSelector';
import GridCellsSelectorContainer from '../grids/GridCellsSelectorContainer';
import StorableSearchInput from '../storables/StorableSearchInput';

export interface ChooseOutputPlateProps {
  errors: Array<string>;
  plate: GridSummary | null;
  registryId?: string;
  selectedCells: Array<AccessibleGridCell>;
  outputSampleStatusState: TransferPaneSampleStatusState;
  sampleStatusWarningTrackingParams: BaseSampleStatusWarningTrackingParams;
  onUpdateSampleStatusState: UpdateTransferPaneSampleStatusStateHandler;
  onUpdateModalState: UpdateTransferModalStateHandler;
}

class ChooseOutputPlate extends React.Component<ChooseOutputPlateProps> {
  private handlePlateChange = (plates: Array<GridSummary>): void => {
    const outputPlate = plates.length ? plates[0] : null;
    this.props.onUpdateModalState({outputPlate, outputCells: []});
    this.props.onUpdateSampleStatusState({shouldShowStatusWarnings: false}, 'outputSampleStatusState');
  };

  private handleSelectCells = (newSelectedCells: Array<AccessibleGridCell>): void => {
    this.props.onUpdateModalState({outputCells: newSelectedCells});
    this.updateOutputSampleStatusWarnings(newSelectedCells);
  };

  private updateOutputSampleStatusWarnings = (cells: Array<AccessibleGridCell>): void => {
    const containers = cells.map(({container}) => container);
    const prevSelectedIds = this.props.outputSampleStatusState.selectedSampleIds;
    const selectedIds = containers.map(({id}) => id);
    // handleSelectCells is also called when returning to pane, which should not update status
    if (
      selectedIds.length > 0 &&
      isEqual(prevSelectedIds, selectedIds) &&
      this.props.outputSampleStatusState.warningInputOutputType === ModelTypeConstants.PLATE
    ) {
      return;
    }
    const {shouldShowWarning, samples} = checkSamplesForStatusWarnings({samples: containers});
    this.props.onUpdateSampleStatusState(
      {
        shouldShowStatusWarnings: shouldShowWarning,
        canProceedOnSampleStatus: false,
        selectedSampleIds: samples?.map(({id}) => id) ?? [],
        samples: samples ?? null,
        warningInputOutputType: ModelTypeConstants.PLATE,
      },
      'outputSampleStatusState'
    );
  };

  private setCanProceedOnSampleStatus = (canProceed: boolean): void => {
    this.props.onUpdateSampleStatusState({canProceedOnSampleStatus: canProceed}, 'outputSampleStatusState');
  };

  renderPossibleWarning(): JSX.Element | null {
    return this.props.outputSampleStatusState.shouldShowStatusWarnings &&
      this.props.outputSampleStatusState.warningInputOutputType === ModelTypeConstants.PLATE &&
      this.props.outputSampleStatusState.samples != null ? (
      <div
        className="u-small-spacing-top"
        {...getTestProps({component: 'ChooseOutputPlate', element: 'sampleStatusWarning'})}
      >
        <SampleStatusWarningBannerStack
          shouldProceed={this.props.outputSampleStatusState.canProceedOnSampleStatus}
          canDeselectSamples={false}
          onChangeShouldProceed={this.setCanProceedOnSampleStatus}
          samples={this.props.outputSampleStatusState.samples}
          setSelectedSampleIds={noop}
          selectedSampleIds={this.props.outputSampleStatusState.selectedSampleIds}
          trackingParamsWithSampleIds={{
            ...this.props.sampleStatusWarningTrackingParams,
            sampleIds: this.props.selectedCells.map(({container}) => container.id),
          }}
        />
      </div>
    ) : null;
  }

  renderErrorBanners(): JSX.Element | null {
    return (
      <div
        className="u-small-spacing-top u-small-spacing-bottom"
        {...getTestProps({component: 'ChooseOutputPlate', element: 'errorBanners'})}
      >
        <InlineBannerStack
          messages={this.props.errors.map((message) => ({text: message, bannerType: InlineBannerType.ERROR}))}
        />
      </div>
    );
  }

  override render(): JSX.Element {
    return (
      <div {...getTestProps({component: 'ChooseOutputPlate', element: 'root'})}>
        <StorableSearchInput
          onChange={this.handlePlateChange}
          registryId={this.props.registryId}
          selectedStorables={this.props.plate ? [this.props.plate] : []}
          storableTypes={[ModelTypeConstants.BOX, ModelTypeConstants.PLATE]}
        />
        {this.props.plate && (
          <>
            {this.renderErrorBanners()}
            {this.renderPossibleWarning()}
            <GridCellsSelectorContainer
              checkCell={(grid: Grid, cell: GridCellSummary) => isCellOccupiedAndAccessible(grid, cell)}
              gridId={this.props.plate.id}
              invalidCellMessage="Please select a non-empty, accessible position."
              onSelectCells={this.handleSelectCells}
              selectedCells={this.props.selectedCells}
            />
          </>
        )}
      </div>
    );
  }
}

export default ChooseOutputPlate;
